import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../lib/supabase';

interface CustomMessage {
  id: string;
  text: string;
  badge: string;
  badgeColor: 'green' | 'teal' | 'orange' | 'red' | 'gray';
  icon: string;
  enabled: boolean;
}

interface NotificationData {
  type: 'live' | 'conversion' | 'lead' | 'custom';
  name?: string;
  city?: string;
  state?: string;
  service?: string;
  minutes?: number;
  viewers?: number;
  customMsg?: CustomMessage;
}

interface NotifConfig {
  enabled: boolean;
  minInterval: number;
  maxInterval: number;
  displayDuration: number;
  minViewers: number;
  maxViewers: number;
  minMinutes: number;
  maxMinutes: number;
  pauseAfterClose: number;
  typeLive: boolean;
  typeConversion: boolean;
  typeLead: boolean;
  typeCustom: boolean;
  customChance: number;
  communityChance: number;
  customMessages: CustomMessage[];
  scheduleEnabled: boolean;
  scheduleStart: string;
  scheduleEnd: string;
  scheduleDays: number[];
  allDay24h?: boolean;
  liveMainText?: string;
  liveSubText?: string;
  // Posição
  position?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
  // Agendamento independente por tipo
  liveScheduleEnabled?: boolean;
  liveAllDay24h?: boolean;
  liveScheduleStart?: string;
  liveScheduleEnd?: string;
  liveScheduleDays?: number[];
  leadScheduleEnabled?: boolean;
  leadAllDay24h?: boolean;
  leadScheduleStart?: string;
  leadScheduleEnd?: string;
  leadScheduleDays?: number[];
  // Intervalo por tipo
  liveMinInterval?: number;
  liveMaxInterval?: number;
  leadMinInterval?: number;
  leadMaxInterval?: number;
}

const STORAGE_KEY = 'ibx_notif_config';

const DEFAULT_CONFIG: NotifConfig = {
  enabled: true,
  minInterval: 10,
  maxInterval: 20,
  displayDuration: 6,
  minViewers: 8,
  maxViewers: 58,
  minMinutes: 2,
  maxMinutes: 59,
  pauseAfterClose: 5,
  typeLive: true,
  typeConversion: true,
  typeLead: true,
  typeCustom: false,
  customChance: 20,
  communityChance: 30,
  customMessages: [],
  scheduleEnabled: false,
  scheduleStart: '08:00',
  scheduleEnd: '20:00',
  scheduleDays: [1, 2, 3, 4, 5],
  allDay24h: false,
  liveMainText: '{n} pessoas estão vendo agora',
  liveSubText: 'Alta demanda neste momento',
  position: 'bottom-left',
  liveScheduleEnabled: false,
  liveAllDay24h: true,
  liveScheduleStart: '08:00',
  liveScheduleEnd: '20:00',
  liveScheduleDays: [1, 2, 3, 4, 5],
  leadScheduleEnabled: false,
  leadAllDay24h: true,
  leadScheduleStart: '08:00',
  leadScheduleEnd: '20:00',
  leadScheduleDays: [1, 2, 3, 4, 5],
  liveMinInterval: 10,
  liveMaxInterval: 20,
  leadMinInterval: 10,
  leadMaxInterval: 20,
};

async function loadConfig(): Promise<NotifConfig> {
  if (!supabase) {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return { ...DEFAULT_CONFIG, ...parsed, customMessages: parsed.customMessages ?? [] };
      } catch {
        return DEFAULT_CONFIG;
      }
    }
    return DEFAULT_CONFIG;
  }

  try {
    const { data, error } = await supabase
      .from('ibx_notif_config')
      .select('config')
      .maybeSingle();

    if (error) {
      console.error('Erro ao carregar config do Supabase:', error);
      return DEFAULT_CONFIG;
    }

    if (!data || !data.config) {
      return DEFAULT_CONFIG;
    }

    return { ...DEFAULT_CONFIG, ...data.config, customMessages: data.config.customMessages ?? [] };
  } catch (err) {
    console.error('Erro ao carregar notificações:', err);
    return DEFAULT_CONFIG;
  }
}

function checkSchedule(
  allDay: boolean | undefined,
  schedEnabled: boolean | undefined,
  days: number[] | undefined,
  start: string | undefined,
  end: string | undefined,
): boolean {
  if (allDay) return true;
  if (!schedEnabled) return true;
  const now = new Date();
  const currentDay = now.getDay();
  const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  return (
    (days ?? []).includes(currentDay) &&
    currentTime >= (start ?? '00:00') &&
    currentTime <= (end ?? '23:59')
  );
}

function isTypeAllowed(type: 'live' | 'lead', cfg: NotifConfig): boolean {
  if (type === 'live') {
    if (cfg.liveScheduleEnabled) {
      return checkSchedule(cfg.liveAllDay24h, true, cfg.liveScheduleDays, cfg.liveScheduleStart, cfg.liveScheduleEnd);
    }
    return checkSchedule(cfg.allDay24h, cfg.scheduleEnabled, cfg.scheduleDays, cfg.scheduleStart, cfg.scheduleEnd);
  }
  if (type === 'lead') {
    if (cfg.leadScheduleEnabled) {
      return checkSchedule(cfg.leadAllDay24h, true, cfg.leadScheduleDays, cfg.leadScheduleStart, cfg.leadScheduleEnd);
    }
    return checkSchedule(cfg.allDay24h, cfg.scheduleEnabled, cfg.scheduleDays, cfg.scheduleStart, cfg.scheduleEnd);
  }
  return true;
}

function getPositionClasses(position: NotifConfig['position']): string {
  switch (position) {
    case 'bottom-right': return 'bottom-6 right-6';
    case 'top-left':     return 'top-6 left-6';
    case 'top-right':    return 'top-6 right-6';
    default:             return 'bottom-6 left-6';
  }
}

function getEnterAnimation(position: NotifConfig['position']): string {
  switch (position) {
    case 'top-left':
    case 'top-right':
      return 'opacity-100 translate-y-0';
    default:
      return 'opacity-100 translate-y-0';
  }
}

function getExitAnimation(position: NotifConfig['position']): string {
  switch (position) {
    case 'top-left':
    case 'top-right':
      return 'opacity-0 -translate-y-4';
    default:
      return 'opacity-0 translate-y-4';
  }
}

function getBadgeClasses(color: CustomMessage['badgeColor']) {
  const map: Record<CustomMessage['badgeColor'], string> = {
    green: 'bg-green-50 text-green-700',
    teal: 'bg-teal-50 text-teal-700',
    orange: 'bg-orange-50 text-orange-700',
    red: 'bg-red-50 text-red-700',
    gray: 'bg-gray-100 text-gray-700',
  };
  return map[color];
}

function getBarColor(color: CustomMessage['badgeColor']) {
  const map: Record<CustomMessage['badgeColor'], string> = {
    green: 'bg-green-500',
    teal: 'bg-teal-500',
    orange: 'bg-orange-500',
    red: 'bg-red-500',
    gray: 'bg-gray-400',
  };
  return map[color];
}

function getIconBg(color: CustomMessage['badgeColor']) {
  const map: Record<CustomMessage['badgeColor'], string> = {
    green: 'bg-green-50 text-green-600',
    teal: 'bg-teal-50 text-teal-600',
    orange: 'bg-orange-50 text-orange-600',
    red: 'bg-red-50 text-red-600',
    gray: 'bg-gray-100 text-gray-600',
  };
  return map[color];
}

const NAMES = [
  'Rafael', 'Ana', 'Carlos', 'Juliana', 'Fernando', 'Mariana', 'Pedro', 'Camila',
  'Lucas', 'Beatriz', 'Rodrigo', 'Fernanda', 'Bruno', 'Patricia', 'Gustavo', 'Amanda',
  'Thiago', 'Larissa', 'Diego', 'Renata', 'Marcelo', 'Gabriela', 'André', 'Vanessa',
  'Felipe', 'Carolina', 'Ricardo', 'Daniela', 'Vinicius', 'Leticia', 'Fabio', 'Priscila',
  'Leandro', 'Tatiana', 'Mauricio', 'Claudia', 'Alexandre', 'Simone', 'Roberto', 'Adriana',
  'Paulo', 'Monica', 'Sergio', 'Cristina', 'Eduardo', 'Sandra', 'Marcos', 'Luciana',
  'Antonio', 'Silvia', 'João', 'Elaine', 'José', 'Rosana', 'Daniel', 'Vera',
  'Henrique', 'Carla', 'Cesar', 'Denise', 'Luiz', 'Regina'
];

const LOCATIONS = [
  { city: 'São Paulo', state: 'SP' }, { city: 'Campinas', state: 'SP' },
  { city: 'Santos', state: 'SP' }, { city: 'Ribeirão Preto', state: 'SP' },
  { city: 'Rio de Janeiro', state: 'RJ' }, { city: 'Belo Horizonte', state: 'MG' },
  { city: 'Curitiba', state: 'PR' }, { city: 'Porto Alegre', state: 'RS' },
  { city: 'Salvador', state: 'BA' }, { city: 'Fortaleza', state: 'CE' },
  { city: 'Recife', state: 'PE' }, { city: 'Brasília', state: 'DF' },
  { city: 'Goiânia', state: 'GO' }, { city: 'Manaus', state: 'AM' },
  { city: 'Vitória', state: 'ES' }, { city: 'Florianópolis', state: 'SC' },
  { city: 'Natal', state: 'RN' }, { city: 'João Pessoa', state: 'PB' },
  { city: 'Maceió', state: 'AL' }, { city: 'Campo Grande', state: 'MS' },
  { city: 'Londrina', state: 'PR' }, { city: 'Niterói', state: 'RJ' },
  { city: 'Uberlândia', state: 'MG' }, { city: 'Sorocaba', state: 'SP' },
  { city: 'Joinville', state: 'SC' }, { city: 'Blumenau', state: 'SC' },
  { city: 'Pelotas', state: 'RS' }, { city: 'Foz do Iguaçu', state: 'PR' },
  { city: 'Bauru', state: 'SP' }, { city: 'Jundiaí', state: 'SP' },
];

const SERVICES = [
  'Suporte Técnico', 'Suporte Estratégico', 'Suporte Premium',
  'Consultoria Técnica', 'Gestão de Contratos', 'Recuperação de Sinistros'
];

const LEAD_ACTIONS = [
  'solicitou análise gratuita', 'iniciou conversa no WhatsApp',
  'solicitou orçamento', 'pediu contato urgente'
];

const SocialProofNotifications = () => {
  const [cfg, setCfg] = useState<NotifConfig | null>(null);
  const [notification, setNotification] = useState<NotificationData | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [heroLeft, setHeroLeft] = useState(false);
  const usedNamesRef = useRef<number[]>([]);
  const usedLocationsRef = useRef<number[]>([]);

  // ✅ ESSA LINHA FALTAVA
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    const handle = (event: any) => {
      setEnabled(!event.detail.disabled);
    };

    window.addEventListener('ibx-chat-notifications', handle);

    return () => {
      window.removeEventListener('ibx-chat-notifications', handle);
    };
  }, []);

  // Carrega config inicial do Supabase
  useEffect(() => {
    loadConfig().then(setCfg);
  }, []);

  // Escuta mudanças em tempo real via Supabase Realtime
  useEffect(() => {
    if (!supabase) return;

    const channel = supabase
      .channel('notif-config-changes-feature')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ibx_notif_config'
        },
        async () => {
          const freshConfig = await loadConfig();
          setCfg(freshConfig);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const getRandomIndex = (array: unknown[], usedList: number[]): number => {
    const available = array.map((_, i) => i).filter(i => !usedList.includes(i));
    if (available.length === 0) return Math.floor(Math.random() * array.length);
    return available[Math.floor(Math.random() * available.length)];
  };

  const generateNotification = useCallback(async (): Promise<NotificationData | null> => {
    const freshCfg = await loadConfig();

    const enabledCustom = freshCfg.customMessages.filter(m => m.enabled);
    if (freshCfg.typeCustom && enabledCustom.length > 0 && Math.random() * 100 < freshCfg.customChance) {
      const msg = enabledCustom[Math.floor(Math.random() * enabledCustom.length)];
      return { type: 'custom', customMsg: msg };
    }

    const activeTypes: ('live' | 'conversion' | 'lead')[] = [];
    if (freshCfg.typeLive && isTypeAllowed('live', freshCfg)) activeTypes.push('live');
    if (freshCfg.typeConversion) activeTypes.push('conversion');
    if (freshCfg.typeLead && isTypeAllowed('lead', freshCfg)) activeTypes.push('lead');
    if (activeTypes.length === 0) return null;

    const type = activeTypes[Math.floor(Math.random() * activeTypes.length)];

    if (type === 'live') {
      return {
        type: 'live',
        viewers: Math.floor(Math.random() * (freshCfg.maxViewers - freshCfg.minViewers + 1)) + freshCfg.minViewers
      };
    }

    const nameIndex = getRandomIndex(NAMES, usedNamesRef.current);
    const locationIndex = getRandomIndex(LOCATIONS, usedLocationsRef.current);
    usedNamesRef.current = [...usedNamesRef.current.slice(-10), nameIndex];
    usedLocationsRef.current = [...usedLocationsRef.current.slice(-10), locationIndex];

    const location = LOCATIONS[locationIndex];
    const minutes = Math.floor(Math.random() * (freshCfg.maxMinutes - freshCfg.minMinutes + 1)) + freshCfg.minMinutes;

    if (type === 'conversion') {
      const isCommunity = Math.random() * 100 < freshCfg.communityChance;
      return {
        type: 'conversion',
        name: NAMES[nameIndex],
        city: location.city,
        state: location.state,
        service: isCommunity ? '__community__' : SERVICES[Math.floor(Math.random() * SERVICES.length)],
        minutes
      };
    }

    return {
      type: 'lead',
      name: NAMES[nameIndex],
      city: location.city,
      state: location.state,
      service: LEAD_ACTIONS[Math.floor(Math.random() * LEAD_ACTIONS.length)],
      minutes
    };
  }, []);

  const getNextInterval = async (): Promise<number> => {
    const freshCfg = await loadConfig();
    const liveMin = freshCfg.liveMinInterval ?? freshCfg.minInterval;
    const liveMax = freshCfg.liveMaxInterval ?? freshCfg.maxInterval;
    const leadMin = freshCfg.leadMinInterval ?? freshCfg.minInterval;
    const leadMax = freshCfg.leadMaxInterval ?? freshCfg.maxInterval;
    const avgMin = Math.round((liveMin + leadMin + freshCfg.minInterval) / 3);
    const avgMax = Math.round((liveMax + leadMax + freshCfg.maxInterval) / 3);
    return Math.random() * (avgMax - avgMin) * 1000 + avgMin * 1000;
  };

  const showNotification = useCallback(async () => {
    if (isPaused || !cfg) return;
    const freshCfg = await loadConfig();
    setCfg(freshCfg);
    if (!freshCfg.enabled) return;

    const generalOk = checkSchedule(freshCfg.allDay24h, freshCfg.scheduleEnabled, freshCfg.scheduleDays, freshCfg.scheduleStart, freshCfg.scheduleEnd);
    if (!generalOk) return;

    const newNotification = await generateNotification();
    if (!newNotification) return;

    setNotification(newNotification);
    setIsVisible(true);

    setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => setNotification(null), 300);
    }, freshCfg.displayDuration * 1000);
  }, [isPaused, cfg, generateNotification]);

  const handleClose = () => {
    if (!cfg) return;
    setIsVisible(false);
    setIsPaused(true);
    setTimeout(() => setNotification(null), 300);
    setTimeout(() => setIsPaused(false), cfg.pauseAfterClose * 60 * 1000);
  };

  // ─── Detecta saída da sessão Hero via IntersectionObserver ───────────────────
  useEffect(() => {
    if (!cfg || !cfg.enabled) return;

    // Tenta encontrar o elemento Hero pelos seletores mais comuns do projeto
    const heroEl =
      document.querySelector<HTMLElement>('[data-section="hero"]') ??
      document.querySelector<HTMLElement>('#hero') ??
      document.querySelector<HTMLElement>('section:first-of-type') ??
      document.querySelector<HTMLElement>('header');

    if (!heroEl) {
      // Fallback: se não encontrar o Hero, aguarda 5s e libera
      const t = setTimeout(() => setHeroLeft(true), 5000);
      return () => clearTimeout(t);
    }

    let delayTimer: ReturnType<typeof setTimeout> | null = null;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Quando o Hero sai completamente da viewport (não está mais visível)
        if (!entry.isIntersecting) {
          if (delayTimer) return; // já agendado
          delayTimer = setTimeout(() => {
            setHeroLeft(true);
            observer.disconnect();
          }, 3000); // aguarda 3 segundos após sair da Hero
        } else {
          // Se o usuário voltar para a Hero, cancela o timer
          if (delayTimer) {
            clearTimeout(delayTimer);
            delayTimer = null;
          }
        }
      },
      {
        threshold: 0,       // dispara assim que qualquer pixel sair
        rootMargin: '0px',  // sem margem extra
      }
    );

    observer.observe(heroEl);

    return () => {
      observer.disconnect();
      if (delayTimer) clearTimeout(delayTimer);
    };
  }, [cfg]);

  // ─── Dispara a primeira notificação somente após heroLeft = true ─────────────
  useEffect(() => {
    if (!heroLeft || !cfg || !cfg.enabled) return;
    // Pequeno jitter aleatório (0–2s) para não parecer mecânico
    const jitter = Math.random() * 2000;
    const t = setTimeout(() => showNotification(), jitter);
    return () => clearTimeout(t);
  }, [heroLeft, cfg, showNotification]);

  // ─── Ciclo contínuo de notificações após a primeira ──────────────────────────
  useEffect(() => {
    if (!heroLeft || !cfg) return;
    if (!notification && !isPaused && cfg.enabled) {
      getNextInterval().then(interval => {
        const t = setTimeout(() => showNotification(), interval);
        return () => clearTimeout(t);
      });
    }
  }, [notification, isPaused, cfg, heroLeft, showNotification]);

  if (!cfg || !cfg.enabled || !enabled || !notification) return null;

  const posClass = getPositionClasses(cfg.position);
  const visibleClass = isVisible ? getEnterAnimation(cfg.position) : getExitAnimation(cfg.position);

  return (
    <div
      className={`fixed z-[9999] transition-all duration-300 ease-out ${posClass} ${visibleClass}`}
      style={{ maxWidth: 'calc(100vw - 3rem)' }}
    >
      <div className="bg-white rounded-lg shadow-2xl border border-gray-100 overflow-hidden w-80 max-w-full">
        <div className="p-4 pr-12 relative">
          <button
            onClick={handleClose}
            className="absolute top-2 right-2 w-11 h-11 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
            aria-label="Fechar notificação"
          >
            <i className="ri-close-line text-lg" aria-hidden="true"></i>
          </button>

          {/* AO VIVO */}
          {notification.type === 'live' && (
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-red-50 rounded-full flex-shrink-0 mt-0.5">
                <i className="ri-live-line text-red-500 text-xl"></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-900">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                    AO VIVO
                  </span>
                </div>
                <p className="text-base font-bold text-gray-900 mb-0.5">
                  {(cfg.liveMainText ?? '{n} pessoas estão vendo agora').replace('{n}', String(notification.viewers ?? 0))}
                </p>
                <p className="text-xs text-gray-500">
                  {cfg.liveSubText ?? 'Alta demanda neste momento'}
                </p>
              </div>
            </div>
          )}

          {/* CONVERSÃO */}
          {notification.type === 'conversion' && (
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 ${notification.service === '__community__' ? 'bg-teal-50' : 'bg-green-50'}`}>
                <i className={`text-xl ${notification.service === '__community__' ? 'ri-group-line text-teal-600' : 'ri-checkbox-circle-line text-green-600'}`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  {notification.service === '__community__' ? (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full">
                      <i className="ri-star-line text-xs"></i>COMUNIDADE
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                      <i className="ri-check-line text-xs"></i>CONTRATADO
                    </span>
                  )}
                </div>
                <p className="text-sm font-bold text-gray-900 mb-0.5 truncate">
                  {notification.name}, {notification.city}-{notification.state}
                </p>
                <p className="text-xs text-gray-600 leading-relaxed">
                  {notification.service === '__community__'
                    ? <>entrou para a <span className="font-semibold">Comunidade Exclusiva</span> há {notification.minutes} minutos</>
                    : <>acabou de contratar o <span className="font-semibold">{notification.service}</span> há {notification.minutes} minutos</>
                  }
                </p>
              </div>
            </div>
          )}

          {/* LEAD */}
          {notification.type === 'lead' && (
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-[#0F3D3E]/10 rounded-full flex-shrink-0 mt-0.5">
                <i className="ri-mail-line text-[#0F3D3E] text-xl"></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#0F3D3E] bg-[#0F3D3E]/10 px-2 py-0.5 rounded-full">
                    <i className="ri-user-add-line text-xs"></i>NOVO CONTATO
                  </span>
                </div>
                <p className="text-sm font-bold text-gray-900 mb-0.5 truncate">
                  {notification.name}, {notification.city}-{notification.state}
                </p>
                <p className="text-xs text-gray-600">
                  {notification.service} há {notification.minutes} minutos
                </p>
              </div>
            </div>
          )}

          {/* PERSONALIZADA */}
          {notification.type === 'custom' && notification.customMsg && (
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 ${getIconBg(notification.customMsg.badgeColor)}`}>
                <i className={`${notification.customMsg.icon} text-xl`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full ${getBadgeClasses(notification.customMsg.badgeColor)}`}>
                    <i className={`${notification.customMsg.icon} text-xs`}></i>
                    {notification.customMsg.badge}
                  </span>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {notification.customMsg.text}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="h-1 bg-gray-100">
          <div
            className={`h-full ${
              notification.type === 'live' ? 'bg-red-500' :
              notification.type === 'conversion' && notification.service === '__community__' ? 'bg-teal-500' :
              notification.type === 'conversion' ? 'bg-green-500' :
              notification.type === 'lead' ? 'bg-[#0F3D3E]' :
              notification.type === 'custom' && notification.customMsg ? getBarColor(notification.customMsg.badgeColor) :
              'bg-gray-400'
            }`}
            style={{ animation: `shrink ${cfg.displayDuration * 1000}ms linear forwards` }}
          />
        </div>
      </div>

      <style>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
};

export default SocialProofNotifications;
